/**
 * ImageCollectionGrid Component
 *
 * Displays images in a grid layout with badges and pagination.
 */
import { Badge } from '@/components/v2/ui';
import { Button } from '@/components/v2/ui';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { ProductImageAsset } from '@/types/image-collection';

interface ImageCollectionGridProps {
  images: ProductImageAsset[];
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  onImageClick: (assetId: string, linkId?: string) => void;
  page: number;
  totalPages: number;
  totalCount: number;
  onPageChange: (page: number) => void;
}

export function ImageCollectionGrid({
  images,
  isLoading,
  isFetching,
  error,
  onImageClick,
  page,
  totalPages,
  totalCount,
  onPageChange,
}: ImageCollectionGridProps) {
  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-sm text-red-600 dark:text-red-400">
            Error loading images
          </p>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
            {error.message}
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-neutral-900 dark:border-neutral-50" />
      </div>
    );
  }

  if (images.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            No images found
          </p>
          <p className="text-xs text-neutral-500 dark:text-neutral-500 mt-1">
            Try adjusting your filters
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50">
            Image Collection
          </h2>
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            {totalCount} image{totalCount !== 1 ? 's' : ''} found
          </p>
        </div>
        {isFetching && (
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-neutral-900 dark:border-neutral-50" />
        )}
      </div>

      {/* Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
        {images.map((image) => (
          <div
            key={image.asset_id}
            className="group relative aspect-square bg-neutral-100 dark:bg-neutral-800 rounded-lg overflow-hidden cursor-pointer hover:ring-2 hover:ring-neutral-400 dark:hover:ring-neutral-600 transition-all"
            onClick={() => onImageClick(image.asset_id, image.link_id)}
          >
            {/* Image */}
            <img
              src={image.asset_id ? `/api/v1/asset/${image.asset_id}/image` : '/images/placeholder-image.png'}
              alt=""
              className="w-full h-full object-cover"
              loading="lazy"
            />

            {/* Badges Overlay */}
            <div className="absolute top-2 left-2 flex flex-col gap-1">
              {image.upload_source === 'integration_sync' && (
                <Badge className="bg-primary-500 text-white text-xs">
                  JTL Original
                </Badge>
              )}
              {image.link_status === 'current' && (
                <Badge className="bg-green-500 text-white text-xs">
                  Currently used
                </Badge>
              )}
              {image.link_status === 'historical' && (
                <Badge className="bg-neutral-500 text-white text-xs">
                  Historical
                </Badge>
              )}
              {image.is_ai_generated && (
                <Badge className="bg-primary-500 text-white text-xs">
                  AI Generated
                </Badge>
              )}
            </div>

            {/* Bottom Info */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
              {/* Tags */}
              {image.tags && image.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-1">
                  {image.tags.slice(0, 3).map((tag) => (
                    <div
                      key={tag.id}
                      className="px-1.5 py-0.5 rounded text-xs text-white"
                      style={{ backgroundColor: tag.color }}
                    >
                      {tag.name}
                    </div>
                  ))}
                  {image.tags.length > 3 && (
                    <div className="px-1.5 py-0.5 rounded text-xs bg-neutral-700 text-white">
                      +{image.tags.length - 3}
                    </div>
                  )}
                </div>
              )}

              {/* Channel */}
              {image.channel_name && (
                <p className="text-xs text-white">
                  {image.channel_name}
                </p>
              )}

              {/* Dimensions */}
              <p className="text-xs text-neutral-300">
                {image.width} × {image.height}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(page - 1)}
            disabled={page === 1}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Previous
          </Button>

          <span className="text-sm text-neutral-600 dark:text-neutral-400">
            Page {page} of {totalPages}
          </span>

          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(page + 1)}
            disabled={page === totalPages}
          >
            Next
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      )}
    </div>
  );
}
